import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { AllEvents } from '@core/models/all-events.model';
import { Pagination } from '@core/models/pagination.model';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { TransactionHttpService } from '@core/services/transaction-http.service';
import { AllEventsDataService } from './services/all-events-data.service';
import { AllEventsEntityService } from './services/all-events-entity.service';
import { Observable, Subscription, of } from 'rxjs';
import { exhaustMap, map, tap } from 'rxjs/operators';
import { LoadingBarService } from '@ngx-loading-bar/core';
import * as moment from 'moment-timezone';
import { MatDialog } from '@angular/material/dialog';
import { AllEventsEditDialogComponent } from './dialogs/all-events-edit/all-events-edit.component';
import { UpdateResultDialogComponent } from './dialogs/update-result/update-result.component';
import { EventTeamsDialogComponent } from './dialogs/event-teams/event-teams.component';
import { AppPermissionService } from "@core/services/app-permission.service";

@Component({
  selector: 'kt-all-events',
  templateUrl: './all-events.component.html',
  styleUrls: ['./all-events.component.scss']
})
export class AllEventsComponent implements OnInit, OnDestroy {

  form: FormGroup;
  events$: Observable<AllEvents[]>;
  dropdown = {
    perPage: this.dropdownHttpService.perPage,
    predictionTypes: this.dropdownHttpService.predictionTypes
  };
  dateTimePickerLocale = this.transactionHttpService.dateTimePickerLocale;
  timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
  ranges = this.transactionHttpService.ranges;

  pagination: Pagination;
  pageSize = 30;
  page = 1;
  maxSize = 5;
  params = '';
  dataLength = 0;
  loading = false;
  clearBtnLoading = false;
  searchBtnLoading = false;
  currencies = JSON.parse(sessionStorage.getItem('currencies')) === null ? [] : JSON.parse(sessionStorage.getItem('currencies'));

  // permissions
  canCreateEvent: boolean;
  canViewEventDetails: boolean;
  canViewTeamPlayerSettings: boolean;
  canSetResult: boolean;

  private subscriptions = new Subscription();

  constructor(
    private allEventsDataService: AllEventsDataService,
    private allEventsEntityService: AllEventsEntityService,
    private dropdownHttpService: DropdownHttpService,
    private loadingBar: LoadingBarService,
    private transactionHttpService: TransactionHttpService,
    public dialog: MatDialog,
    private appPermissionService: AppPermissionService,
  ) { }

  ngOnInit() {
    this.formInit();
    this.pagination = this.allEventsDataService.pagination;
    this.reload();

    const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      this.canCreateEvent = appPermissions.create_event;
      this.canViewEventDetails = appPermissions.view_event_details;
      this.canViewTeamPlayerSettings = appPermissions.view_team_player_settings;
      this.canSetResult = appPermissions.set_result;
    });

    this.subscriptions.add(apSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onDateRange(event: any) {
    if (event) {
      this.form.patchValue({
        start_datetime: event.startDate !== null && event.startDate !== undefined ? event.startDate : null,
        end_datetime: event.endDate !== null && event.endDate !== undefined ? event.endDate : null
      });
    }
  }

  onClearDate() {
    if (this.form.value.start_datetime !== null) {
      this.form.patchValue({ defaultDate: null });
    }
  }

  onSubmit(clearSearch?: boolean) {
    this.timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
    this.dataLength = 0;
    this.searchBtnLoading = clearSearch ? false : true;
    this.loading = true;
    of(this.form.value).pipe(
      map(this.filterFormFields),
      exhaustMap((data) => {
        if (data['start_datetime']) {
          data['start_datetime'] = moment(data['start_datetime']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
          data['end_datetime'] = moment(data['end_datetime']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
        }

        this.params = Object.keys(data).map(key => key + '=' + data[key]).join('&');
        this.loadingBar.start();
        return this.events$ = this.allEventsEntityService.getWithQuery(`?${this.params}&perPage=${this.pageSize}`).pipe(
          tap(res => {
            this.dataLength = res.length;
            this.loading = false;
            this.clearBtnLoading = false;
            this.searchBtnLoading = false;
            this.page = 1;
            this.pagination = this.allEventsDataService.pagination;
            this.loadingBar.complete();
          })
        );
      }),
    ).subscribe();
  }

  onClear() {
    this.clearBtnLoading = true;
    this.form.patchValue({
      name: null,
      prediction_type: 'all',
      defaultDate: null,
      start_datetime: null,
      end_datetime: null,
      date_type: 'publish_date',
    });
    this.onSubmit(true);
  }

  onViewPageBy(page = 1, pageSize?: number, params?: string) {
    this.loading = true;
    pageSize = this.pageSize;
    params = this.params ? `&${this.params}` : '';
    this.loadingBar.start();
    return this.events$ = this.allEventsEntityService.getWithQuery(`?page=${page}&perPage=${pageSize}${params}`).pipe(
      tap(res => {
        this.dataLength = res.length;
        this.loading = false;
        this.pagination = this.allEventsDataService.pagination;
        this.loadingBar.complete();
      })
    );
  }

  onPerPage(size: Event) {
    this.pageSize = +(size.target as HTMLSelectElement).value;
    this.onViewPageBy(this.page, this.pageSize, this.params);
  }

  onOpenDialog(mode: string, row?: any) {
    const selectCurrency = () => {
      this.openDialogBy(AllEventsEditDialogComponent, { row: row, mode: mode, currencies: this.currencies });
    };
    if (mode === 'edit' || mode === 'create') {
      if(this.currencies.length === 0){
        this.dropdownHttpService.currencies.subscribe( res => {
          this.currencies = res;
          selectCurrency();
        });
      }else{
        selectCurrency();
      }
    } else if (mode === 'teams') {
      this.openDialogBy(EventTeamsDialogComponent, { row: row });
     }
    else {
      this.openDialogBy(UpdateResultDialogComponent, { row: {id: row.id} });
    }
  }

  updateDateRange() {
    this.ranges = this.transactionHttpService.updateDateRange();
  }

  private openDialogBy(componentRef: any, data?: { row: any, mode?: any, currencies?: any }) {
    const dialogRef = this.dialog.open(componentRef, {
      width: '1200px',
      data: {
        row: data.row,
        mode: data.mode,
        currencies: data.currencies
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.onViewPageBy(this.page).subscribe();
      }
    });
  }

  private reload() {
    this.onViewPageBy(this.page).subscribe();
  }

  private filterFormFields(formData: any) {
    const fields = {};
    Object.keys(formData).forEach(key => {
      if (formData[key] !== '' && formData[key] !== null && formData[key] !== undefined && key !== 'defaultDate' && formData[key] !== 'all') {
        fields[key] = formData[key];
      }
    });
    return fields;
  }

  private formInit() {
    this.form = new FormGroup({
      name: new FormControl(null),
      prediction_type: new FormControl('all'),
      defaultDate: new FormControl(null), // Do not remove: For Clearing The Range
      start_datetime: new FormControl(null),
      end_datetime: new FormControl(null),
      date_type: new FormControl('publish_date'),
    });
  }

}
